import { createAction, createSelector } from '@reduxjs/toolkit';
import {
  AlarmCall,
  AlarmCallEvent,
  AlarmCallParticipant,
  AlarmCallType,
  AlarmLogActionCallKind,
  AlarmLogEvent,
  AlarmLogEventKind,
  AlarmLogProtocolKind,
} from '@x-guard/xgac-types/xgac';
import {
  ChatState,
  DispatchLogOperatorAction,
  LogOperatorActionArgs,
  Page,
  RS,
} from 'types';
import { AsyncStatus } from 'types/utils';
import {
  advancedQs, api, createSlice, createThunk, isAbortError,
} from 'lib';
import { alarmDetailsSelector } from 'features/details/exports';

const name = 'details/chat';

const heartbeatMs = 7000;

export const upsertCallEvent = createAction<AlarmCallEvent>(`${name}/upsertCallEvent`);
export const removeCallEvent = createAction<string>(`${name}/removeCallEvent`);
export const upsertParticipant = createAction<AlarmCallParticipant>(`${name}/upsertParticipant`);
export const removeParticipant = createAction<string>(`${name}/removeParticipant`);
export const upsertLogEvent = createAction<AlarmLogEvent>(`${name}/upsertLogEvent`);
export const addCallId = createAction<string>(`${name}/addCallId`);
export const closeHelperCall = createAction<string>(`${name}/closeHelperCall`);

const initialState = {
  status: AsyncStatus.Idle,
  callEvents: [],
  participants: [],
  operatorMessages: [],
  optimisticOperator: [],
  logs: [],
  callIds: [],
  openHelperCallIds: [],
  error: null,
};

const upsertById = <T extends { _id: string }>(list: T[], item: T) => {

  const index = list.findIndex((row) => row._id === item._id);

  if (index === -1) {

    list.unshift(item);
    return;

  }

  list[index] = item;

};

const readList = async <T>(url: string, signal: AbortSignal): Promise<T[]> => {

  const { data } = await api.get<Page<T> | T[]>(url, { signal });

  if (Array.isArray(data)) return data;

  return data.result ?? [];

};

export const refreshLogEvent = createThunk<void, { alarmId: string; eventId: string }>({
  name: `${name}/refreshLogEvent`,
  handler: async ({ alarmId, eventId }, { dispatch }) => {

    const rows = await readList<AlarmLogEvent>(
      `/alarms/${alarmId}/alarm-log-events?${advancedQs({ _id: eventId, $max: 1 })}`,
      new AbortController().signal,
    );

    if (rows[0]) dispatch(upsertLogEvent(rows[0]));

  },
});

const isOperatorChat = (event: AlarmLogEvent): boolean => event.data.eventType === AlarmLogEventKind.OperatorChat;

const textOf = (meta: Record<string, unknown> | undefined, key: string) => {

  const value = meta?.[key];

  return typeof value === 'string' ? value : undefined;

};

const callKind = (action: string): AlarmLogActionCallKind => {

  switch (action) {

  case 'ACTION_CALL_RESULT_CANCELLED':
    return AlarmLogActionCallKind.ResultCancelled;
  case 'ACTION_CALL_RESULT_NOT_REACHABLE':
    return AlarmLogActionCallKind.ResultNotReachable;
  case 'ACTION_CALL_RESULT_NO_RESPONSE':
    return AlarmLogActionCallKind.ResultNoResponse;
  case 'ACTION_CALL_RESULT_RESPONDING':
    return AlarmLogActionCallKind.ResultResponding;
  default:
    return AlarmLogActionCallKind.Initiated;

  }

};

const operatorLogData = (args: LogOperatorActionArgs<any>): AlarmLogEvent['data'] => {

  const meta = args.meta as Record<string, unknown> | undefined;

  switch (args.action) {

  case 'ACTION_PROTOCOL_HELP':
    return { eventType: AlarmLogEventKind.Protocol, kind: AlarmLogProtocolKind.Help };
  case 'ACTION_PROTOCOL_VERIFICATION':
    return { eventType: AlarmLogEventKind.Protocol, kind: AlarmLogProtocolKind.Verification };
  case 'ACTION_SMS':
    return {
      eventType: AlarmLogEventKind.Sms,
      name: textOf(meta, 'name') ?? '',
      phone: textOf(meta, 'sms') ?? textOf(meta, 'number') ?? textOf(meta, 'phone') ?? '',
    };
  case 'ACTION_EMAIL':
    return {
      eventType: AlarmLogEventKind.Email,
      name: textOf(meta, 'name') ?? '',
      email: textOf(meta, 'email'),
    };
  default: {

    const targetAssetId = textOf(meta, 'targetAssetId');

    return {
      eventType: AlarmLogEventKind.ActionCall,
      name: textOf(meta, 'name') ?? '',
      phone: textOf(meta, 'phone') ?? textOf(meta, 'number') ?? '',
      kind: callKind(args.action),
      ...(targetAssetId ? {
        targetAsset: { _id: targetAssetId, _ref: 'Asset' as const },
      } : {}),
    };

  }

  }

};

export const logOperatorAction: DispatchLogOperatorAction = createThunk<void, LogOperatorActionArgs<any>>({
  name: `${name}/logOperatorAction`,
  handler: async (args, { getState }) => {

    const currentAlarm = alarmDetailsSelector(getState());

    await api.post(`/alarms/${currentAlarm._id}/alarm-log-events`, {
      data: operatorLogData(args),
    });

  },
});

type FetchChatArgs = {
  alarmId: string;
  controller: AbortController;
};

type FetchChatResult = {
  callEvents: AlarmCallEvent[];
  participants: AlarmCallParticipant[];
  operatorMessages: AlarmLogEvent[];
  logs: AlarmLogEvent[];
  callIds: string[];
  openHelperCallIds: string[];
};

export const fetchAlarmChat = createThunk<FetchChatResult, FetchChatArgs>({
  name,
  handler: async ({ alarmId, controller: { signal } }) => {

    const calls = await readList<AlarmCall>(`/alarms/${alarmId}/calls`, signal);
    const helperCalls = calls
      .filter((call) => call.type === AlarmCallType.HelperCall)
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    const callIds = helperCalls.map((call) => call._id);
    const openHelperCallIds = helperCalls.filter((call) => call.endedAt == null).map((call) => call._id);

    let anyEventPageLoaded = false;
    const eventPages = await Promise.all(callIds.map(async (callId) => {

      try {

        const page = await readList<AlarmCallEvent>(
          `/alarm-calls/${callId}/alarm-call-events?${advancedQs({ $sort: '-createdAt', $max: 200 })}`,
          signal,
        );

        anyEventPageLoaded = true;
        return page;

      } catch (error) {

        if (signal.aborted || (error instanceof Error && isAbortError(error))) throw error;

        return [];

      }

    }));

    if (callIds.length > 0 && !anyEventPageLoaded) {

      throw new Error('Failed to load call events');

    }
    const participantPages = await Promise.all(callIds.map(async (callId) => {

      try {

        return await readList<AlarmCallParticipant>(
          `/alarm-calls/${callId}/alarm-call-participants?${advancedQs({ $max: 200 })}`,
          signal,
        );

      } catch (error) {

        if (signal.aborted || (error instanceof Error && isAbortError(error))) throw error;

        return [];

      }

    }));
    const logEvents = await readList<AlarmLogEvent>(
      `/alarms/${alarmId}/alarm-log-events?${advancedQs({ $sort: '-createdAt', $max: 200 })}`,
      signal,
    );

    return {
      callIds,
      openHelperCallIds,
      callEvents: eventPages.flat(),
      participants: participantPages.flat(),
      operatorMessages: logEvents.filter(isOperatorChat),
      logs: logEvents.filter((event) => !isOperatorChat(event)),
    };

  },
});

export const removeOptimisticOperator = createAction<string>(`${name}/removeOptimisticOperator`);

const dropOneOptimistic = (list: { message: string }[], message: string) => {

  const index = list.findIndex((row) => row.message === message);

  if (index >= 0) list.splice(index, 1);

};

export const appendOptimisticOperator = createAction<{
  _id: string;
  message: string;
  createdAt: string;
  name: string;
}>(`${name}/appendOptimisticOperator`);

export const chat = createSlice<ChatState, typeof name>({
  name,
  initialState,
  extraReducers: (builder) => builder
    .addCase(fetchAlarmChat.pending, (state) => {

      if (state.status.idle) state.status = AsyncStatus.Pending;

      state.error = null;

    })
    .addCase(fetchAlarmChat.fulfilled, (state, action) => {

      if (action.meta.arg.controller.signal.aborted) return;

      state.status = AsyncStatus.Fulfilled;
      const sameCalls = state.callIds.length === action.payload.callIds.length
        && state.callIds.every((id, index) => id === action.payload.callIds[index]);

      if (!sameCalls) state.callIds = action.payload.callIds;
      state.openHelperCallIds = action.payload.openHelperCallIds;
      state.callEvents = action.payload.callEvents;
      state.participants = action.payload.participants;
      state.operatorMessages = action.payload.operatorMessages;
      state.logs = action.payload.logs;
      action.payload.operatorMessages.forEach((event) => {

        if (event.data.eventType === AlarmLogEventKind.OperatorChat) {

          dropOneOptimistic(state.optimisticOperator, event.data.message);

        }

      });

    })
    .addCase(fetchAlarmChat.rejected, (state, action) => {

      if (isAbortError(action.error) || action.meta.arg.controller.signal.aborted) return;

      state.status = AsyncStatus.Rejected;
      state.error = action.error;

    })
    .addCase(upsertCallEvent, (state, action) => {

      upsertById(state.callEvents, action.payload);

    })
    .addCase(removeCallEvent, (state, action) => {

      state.callEvents = state.callEvents.filter((event) => event._id !== action.payload);

    })
    .addCase(upsertParticipant, (state, action) => {

      upsertById(state.participants, action.payload);

    })
    .addCase(removeParticipant, (state, action) => {

      state.participants = state.participants.filter((participant) => participant._id !== action.payload);

    })
    .addCase(upsertLogEvent, (state, action) => {

      if (isOperatorChat(action.payload)) {

        upsertById(state.operatorMessages, action.payload);
        if (action.payload.data.eventType === AlarmLogEventKind.OperatorChat) {

          dropOneOptimistic(state.optimisticOperator, action.payload.data.message);

        }
        return;

      }

      upsertById(state.logs, action.payload);

    })
    .addCase(addCallId, (state, action) => {

      if (!state.callIds.includes(action.payload)) state.callIds.push(action.payload);
      if (!state.openHelperCallIds.includes(action.payload)) state.openHelperCallIds.push(action.payload);

    })
    .addCase(closeHelperCall, (state, action) => {

      state.openHelperCallIds = state.openHelperCallIds.filter((id) => id !== action.payload);

    })
    .addCase(appendOptimisticOperator, (state, action) => {

      state.optimisticOperator.unshift(action.payload);

    })
    .addCase(removeOptimisticOperator, (state, action) => {

      state.optimisticOperator = state.optimisticOperator.filter((row) => row._id !== action.payload);

    }),
});

export const stateSelector = (state: RS) => state.details.chat;

export const callIdsSelector = createSelector(stateSelector, (state) => state.callIds);

export { heartbeatMs };
